import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PdfExportButton from "../components/PdfExportButton";
import { Landmark, RefreshCw, Info } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import MultiSelect from "../components/MultiSelect";
import FniExecutive from "../components/fni/FniExecutive";
import FniBanksTable from "../components/fni/FniBanksTable";
import FniSucursalTable from "../components/fni/FniSucursalTable";
import FniBrandFunnels from "../components/fni/FniBrandFunnels";
import FniClientsTable from "../components/fni/FniClientsTable";
import { CompareLegend, CompareExecutive, CompareTable } from "../components/fni/FniCompare";
import CampaignBanner, { prettyCampaign } from "../components/fni/CampaignBanner";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const DEFAULTS = { year: String(new Date().getFullYear()), month: "Todos", campaign: "Todas", bank: "Todos", sucursal: "Todas", compare: "Ninguna" };

export default function FniPage() {
  const { user } = useAuth();
  if (user && user.role !== "admin") {
    return (
      <div className="p-6" data-testid="fni-no-access">
        <div className="surface p-6 max-w-lg">
          <h2 className="font-display text-lg font-extrabold mb-1">Acceso restringido</h2>
          <p className="text-sm text-gray-500 font-semibold">El dashboard de F&amp;I está en vista previa y solo lo ven administradores.</p>
        </div>
      </div>
    );
  }
  return <FniDashboard/>;
}

function FniDashboard() {
  const [filters, setFilters] = useState(DEFAULTS);
  const [opts, setOpts] = useState({ years: [], campaigns: [], banks: [], sucursales: [] });
  const [data, setData] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [error, setError] = useState("");
  const [selSuc, setSelSuc] = useState(null);
  const [pick, setPick] = useState(null);
  const pageRef = useRef(null);
  const compareOn = filters.compare !== "Ninguna" && filters.campaign !== "Todas";
  const params = useMemo(() => {
    const { compare, ...p } = filters;
    return compareOn ? { ...p, year: "Todos", month: "Todos" } : p;
  }, [filters, compareOn]);
  const paramsB = useMemo(() => (compareOn ? { ...params, campaign: filters.compare } : null), [params, compareOn, filters.compare]);

  useEffect(() => {
    api.get("/fni/filter-options", { params: { year: filters.year } }).then((r) => setOpts(r.data)).catch(() => {});
  }, [filters.year]);

  const load = useCallback(() => {
    setData(null);
    setDataB(null);
    setError("");
    Promise.all([api.get("/fni/overview", { params }), paramsB ? api.get("/fni/overview", { params: paramsB }) : null])
      .then(([a, b]) => { setData(a.data); setDataB(b ? b.data : null); })
      .catch(() => setError("No se pudo cargar el dashboard de F&I"));
  }, [params, paramsB]);
  useEffect(() => { load(); setPick(null); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const suc = data?.sucursales.find((s) => s.name === selSuc);

  const pdfSubtitle = filters.campaign !== "Todas"
    ? filters.campaign.split(",").map((c) => { const p = prettyCampaign(c); return [p.words.join(" "), p.year].filter(Boolean).join(" "); }).join(", ")
    : `Año ${filters.year} · Mes ${filters.month}`;
  const updated = data?.opps_updated_at
    ? new Date(data.opps_updated_at).toLocaleString("es-DO", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="fni-page" ref={pageRef}>
      <header className="surface flex justify-between items-center gap-4 p-5 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="kpi-icon-clean" style={{ color: "#0891b2", boxShadow: "inset 0 0 0 1px #0891b21f" }}><Landmark size={20}/></div>
          <div>
            <h1 className="text-2xl font-extrabold font-display leading-none">F&amp;I · Financiamiento</h1>
            <p className="text-xs text-gray-500 font-semibold mt-1">Evento / Banco → Sucursal → Marca → Cliente</p>
            {updated && filters.campaign === "Todas" && (
              <p className="text-[11px] text-gray-400 font-semibold mt-0.5" data-testid="fni-opps-updated">Oportunidades actualizadas: {updated}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PdfExportButton targetRef={pageRef} filename="fni-financiamiento" title="SDM · F&I Financiamiento"
                           subtitle={pdfSubtitle} testid="fni-pdf-btn"/>
          <button className="btn flex items-center gap-2" onClick={load} data-testid="fni-refresh-btn"><RefreshCw size={16}/></button>
          <button className="btn primary" onClick={() => { setFilters(DEFAULTS); setSelSuc(null); }} data-testid="fni-reset-btn">Borrar Filtros</button>
        </div>
      </header>

      <section className="surface p-4 mb-5 grid gap-3 grid-cols-2 lg:grid-cols-6" data-testid="fni-filters">
        {[["Año", "year", opts.years, "Todos"], ["Mes", "month", MONTHS, "Todos"], ["Campaña", "campaign", opts.campaigns, "Todas"],
          ["Banco", "bank", opts.banks, "Todos"], ["Sucursal", "sucursal", opts.sucursales, "Todas"],
          ["Comparar con campaña", "compare", opts.all_campaigns, "Ninguna"]].map(([label, key, o, all]) => (
          <div key={key} className="min-w-0">
            <label className="label-up block mb-2">{label}</label>
            <MultiSelect value={filters[key]} onChange={(v) => setF(key, v)} placeholder={label} options={o || []}
                         allValue={all} allLabel={all} testid={`fni-filter-${key}`}/>
          </div>
        ))}
      </section>

      {filters.compare !== "Ninguna" && filters.campaign === "Todas" && (
        <div className="text-[12px] font-bold text-gray-500 mb-4" data-testid="fni-compare-hint">Elige primero una campaña en el filtro Campaña para compararla.</div>
      )}
      {error && <div className="pill bad mb-5" data-testid="fni-error">{error}</div>}
      {!data && !error && <div className="grid place-items-center py-16"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}/></div>}
      {data && dataB && (
        <>
          <CampaignBanner campaign={filters.campaign} compare={filters.compare} totals={data.totals} updated={updated}/>
          <CompareLegend a={filters.campaign.split(",").map((c) => { const p = prettyCampaign(c); return [p.words.join(" "), p.year].filter(Boolean).join(" "); }).join(", ")}
                         b={filters.compare.split(",").map((c) => { const p = prettyCampaign(c); return [p.words.join(" "), p.year].filter(Boolean).join(" "); }).join(", ")}/>
          <CompareExecutive a={data.totals} b={dataB.totals}/>
          <CompareTable title="Por banco" first="Banco" rowsA={data.banks} rowsB={dataB.banks} testid="fni-cmp-banks"
                        metrics={[["sometido", "Sometidas"], ["aprobado", "Aprobadas"], ["firmado", "Firmadas"], ["approval_rate", "% Aprob.", "pct"], ["monto_sometido", "RD$ sometido", "rd"]]}/>
          <CompareTable title="Por sucursal y marca" first="Unidad de negocio" rowsA={data.sucursales} rowsB={dataB.sucursales} expandable testid="fni-cmp-sucursales"
                        metrics={[["leads", "Leads"], ["oportunidad", "Oportunidades"], ["sometido", "Sometidas"], ["approval_rate", "% Aprob.", "pct"], ["facturado", "Facturado"]]}/>
          <div className="text-[12px] font-semibold text-gray-500" data-testid="fni-compare-drill-note">Para ver el embudo por marca y la lista de clientes, quita la comparación.</div>
        </>
      )}
      {data && !dataB && (
        <>
          <CampaignBanner campaign={filters.campaign} totals={data.totals} updated={updated}/>
          <FniExecutive t={data.totals} onPick={setPick}/>
          <FniClientsTable pick={pick && !pick.sucursal ? pick : null} params={params} onClose={() => setPick(null)}/>
          <FniBanksTable banks={data.banks} onPick={(b) => setF("bank", b)}/>
          <FniSucursalTable sucursales={data.sucursales} selected={selSuc} onSelect={(s) => setSelSuc(s === selSuc ? null : s)}/>
          <FniBrandFunnels sucursal={suc} onPick={setPick} onClose={() => setSelSuc(null)}/>
          <FniClientsTable pick={pick && pick.sucursal ? pick : null} params={params} onClose={() => setPick(null)}/>
        </>
      )}
      {data && (
        <div className="flex items-start gap-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2" data-testid="fni-pending-note">
          <Info size={14} className="mt-0.5 shrink-0"/>
          Mismas reglas que Sales Performance: Leads incluye Contact Center, Yamaha y Gobierno; Sometidas = oportunidades con Banco lleno; Facturado = todas las unidades (sin Yamaha ni Gobierno). Pendiente de data: banco con el que se facturó cada venta y monto real a financiar (montos estimados con la cotización del cliente).
        </div>
      )}
    </div>
  );
}
